import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, memo } from "react";
import { type DayItem, NurseFullRole, type NurseRole } from "@/utils/models";
import { rolesMap } from "@/utils/utilFunctions";
import { type MissingSlotInfo, ROLE_HEADER_TOP_GAP_PX } from "./consts";
import { LocationHeaderRow } from "./LocationHeaderRow";
import { NurseSlotRow } from "./NurseSlotRow";
import { RoleSectionHeader } from "./RoleSectionHeader";
import { TimeTitleRow } from "./TimeTitleRow";
import type { ComputedLocation, NurseSlot, RoleSection, TimeTitle } from "./useWeeklyGapView";

export type FlatRow =
	| { kind: "role-header"; section: RoleSection; hasExpanded: boolean; isFirst: boolean }
	| { kind: "time-title"; section: RoleSection; title: TimeTitle; titleKey: string; isExpanded: boolean }
	| { kind: "location-header"; cl: ComputedLocation }
	| { kind: "nurse-slot"; cl: ComputedLocation; slotIdx: number; roles: NurseFullRole[]; jobId?: string };

/**
 * Whether a "nurse-slot" row needs the taller two-line height. One nurse-slot row spans all 7
 * `weekViewData` day columns at a fixed `slotIdx` (see `NurseSlotRow`), and each column can hold a
 * different nurse — so the row must grow the moment ANY of its 7 cells renders `NurseCellContent`'s
 * second (actual-hours) line, not just the first day's, or the other days' overflow goes unnoticed.
 */
export const nurseSlotRowNeedsHoursLine = (row: FlatRow): boolean =>
	row.kind === "nurse-slot" && row.cl.dayData.some((day) => day.nurses[row.slotIdx]?.showHours);

export const resolveSectionRoles = (section: RoleSection, roleGroups?: Record<string, string[]>): NurseFullRole[] => {
	const directRole = NurseFullRole[section.shortRole as keyof typeof NurseFullRole];
	if (directRole) return [directRole];
	const groupRoles = roleGroups?.[section.shortRole];
	if (groupRoles) {
		return groupRoles.map((sr) => rolesMap[sr as NurseRole]).filter((r): r is NurseFullRole => r !== undefined);
	}
	return [];
};

type BuildArgs = {
	sections: RoleSection[];
	expandedTitles: Set<string>;
	roleGroups?: Record<string, string[]>;
};

export const buildFlatRows = ({ sections, expandedTitles, roleGroups }: BuildArgs): FlatRow[] => {
	const rows: FlatRow[] = [];
	for (let s = 0; s < sections.length; s++) {
		const section = sections[s];
		const titleKeys = section.timeTitles.map((t) => `${section.shortRole}__${t.label}`);
		const hasExpanded = titleKeys.some((key) => expandedTitles.has(key));
		rows.push({ kind: "role-header", section, hasExpanded, isFirst: s === 0 });
		const sectionRoles = resolveSectionRoles(section, roleGroups);
		for (let i = 0; i < section.timeTitles.length; i++) {
			const title = section.timeTitles[i];
			const titleKey = titleKeys[i];
			const isExpanded = expandedTitles.has(titleKey);
			rows.push({ kind: "time-title", section, title, titleKey, isExpanded });
			if (!isExpanded) continue;
			for (const cl of title.locations) {
				rows.push({ kind: "location-header", cl });
				for (let slotIdx = 0; slotIdx < cl.numSlots; slotIdx++) {
					rows.push({ kind: "nurse-slot", cl, slotIdx, roles: sectionRoles, jobId: section.jobId });
				}
			}
		}
	}
	return rows;
};

type FlatRowRendererProps = {
	row: FlatRow;
	weekViewData: DayItem[];
	searchedNurseId: number | null;
	roleGroups?: Record<string, string[]>;
	onToggleTitle: (key: string) => void;
	onCollapseRole: (titleKeys: string[]) => void;
	onNurseClick: (nurse: NurseSlot, anchor: HTMLElement) => void;
	onNurseDoubleClick: (nurseId: number) => void;
	onMissingClick: (info: MissingSlotInfo) => void;
};

export const FlatRowRenderer: FC<FlatRowRendererProps> = memo(
	({ row, weekViewData, searchedNurseId, roleGroups, onToggleTitle, onCollapseRole, onNurseClick, onNurseDoubleClick, onMissingClick }) => {
		switch (row.kind) {
			case "role-header": {
				const titleKeys = row.section.timeTitles.map((t) => `${row.section.shortRole}__${t.label}`);
				const header = (
					<RoleSectionHeader section={row.section} roleGroups={roleGroups} hasExpanded={row.hasExpanded} onCollapseAll={() => onCollapseRole(titleKeys)} />
				);
				return row.isFirst ? header : <Box sx={{ pt: `${ROLE_HEADER_TOP_GAP_PX}px` }}>{header}</Box>;
			}
			case "time-title": {
				const { titleKey } = row;
				return (
					<TimeTitleRow
						title={row.title}
						section={row.section}
						isExpanded={row.isExpanded}
						onToggle={() => onToggleTitle(titleKey)}
						weekViewData={weekViewData}
					/>
				);
			}
			case "location-header":
				return <LocationHeaderRow cl={row.cl} weekViewData={weekViewData} />;
			case "nurse-slot": {
				const sectionRoles = row.roles;
				const wrappedOnMissingClick = (dayDate: DateTime, locationName: string, startMin: number, endMin: number, existingReqId?: string) =>
					onMissingClick({ dayDate, locationName, startMin, endMin, roles: sectionRoles, jobId: row.jobId, existingReqId });
				return (
					<NurseSlotRow
						cl={row.cl}
						slotIdx={row.slotIdx}
						weekViewData={weekViewData}
						searchedNurseId={searchedNurseId}
						onNurseClick={onNurseClick}
						onNurseDoubleClick={onNurseDoubleClick}
						onMissingClick={wrappedOnMissingClick}
					/>
				);
			}
		}
	},
);
FlatRowRenderer.displayName = "FlatRowRenderer";
